import { getCookie } from 'utils/cookie';

export const COOKIE_CONSENT_SETTINGS_KEY = 'cookie_consent';

export const COOKIE_OPTION = {
  NECESSARY: 'NECESSARY',
  ADVERTISING: 'ADVERTISING',
  ANALYTICS: 'ANALYTICS',
} as const;

export type CookieOptions = {
  [K in (typeof COOKIE_OPTION)[keyof typeof COOKIE_OPTION]]: boolean;
};

export const getCookieConsentSettings = () => {
  const settingsString =
    localStorage?.getItem(COOKIE_CONSENT_SETTINGS_KEY) ?? '{}';
  const settings = JSON.parse(settingsString);

  // If we couldn't locate a preference for all the cookie preferences then the user must choose again.
  if (
    JSON.stringify(Object.keys(COOKIE_OPTION).sort()) !==
    JSON.stringify(Object.keys(settings).sort())
  )
    return null;

  return settings;
};

export const cookieConsentSettingsHaveNotBeenSaved = () => {
  let settings = getCookieConsentSettings();

  if (settings === null) {
    // attempt to get cookies from webflow cookie
    settings = loadWebflowConsents();
  }

  return settings === null;
};

export const loadWebflowConsents = () => {
  try {
    const webflowConsentCookie = getCookie('webflowconsents', null);

    if (webflowConsentCookie) {
      const webflowConsents = JSON.parse(webflowConsentCookie);

      if (webflowConsents !== null) {
        const newConsents = {
          NECESSARY: webflowConsents.essential,
          ADVERTISING: webflowConsents.marketing,
          ANALYTICS: webflowConsents.analytics,
        };

        setCookieConsentSettings(newConsents);

        return newConsents;
      }
    }
  } catch (err) {
    console.log('error', err);
  }

  return null;
};

// Expects settings to be an object containing the various consent options as booleans, e.g:
// { necessary: true, advertising: true, analytics: false }
export const setCookieConsentSettings = (settings: CookieOptions) => {
  // If settings object is not of the correct shape then throw an error.
  if (
    JSON.stringify(Object.keys(COOKIE_OPTION).sort()) !==
    JSON.stringify(Object.keys(settings).sort())
  ) {
    throw new Error(
      `Settings is not of the correct shape: ${JSON.stringify(settings)}`,
    );
  }

  localStorage.setItem(COOKIE_CONSENT_SETTINGS_KEY, JSON.stringify(settings));
  sendAwinAdvertiserConsent(settings[COOKIE_OPTION.ADVERTISING]);
};

// The Awin MasterTag is always loaded, so Awin must be told whether the user consented to advertising cookies.
// See https://help.awin.com/developers/docs/implementing-awin-consent
export const getAwinAdvertiserConsent = (): boolean =>
  getCookieConsentSettings()?.[COOKIE_OPTION.ADVERTISING] === true;

// Must be called before the MasterTag loads, so it knows whether it may set its first party cookies
export const initialiseAwinAdvertiserConsent = () => {
  window.AWIN = window.AWIN || {};
  window.AWIN.Tracking = window.AWIN.Tracking || {};
  window.AWIN.Tracking.AdvertiserConsent = getAwinAdvertiserConsent();
};

// Called when the user interacts with the cookie banner
const sendAwinAdvertiserConsent = (consent: boolean) => {
  if (window.AWIN?.Tracking?.Consent?.setAdvertiserConsentStatus) {
    window.AWIN.Tracking.Consent.setAdvertiserConsentStatus(consent);
  } else {
    // The MasterTag hasn't loaded yet, so it will pick this up when it does
    window.AWIN = window.AWIN || {};
    window.AWIN.Tracking = window.AWIN.Tracking || {};
    window.AWIN.Tracking.AdvertiserConsent = consent;
  }
};
